/** Display metadata for achievement badges and gallery rows. */

export type AchievementCategory = "ads" | "sales" | "kb" | "training" | "user" | "lead" | "prompting";

export type AchievementIconName =
  | "Award"
  | "Flame"
  | "Star"
  | "Trophy"
  | "Zap"
  | "BookOpen"
  | "GraduationCap"
  | "DollarSign"
  | "Sparkles"
  | "Calendar"
  | "LayoutTemplate"
  | "Image"
  | "Target"
  | "MessageCircleQuestion"
  | "UserPlus"
  | "Send";

export type AchievementCatalogEntry = {
  label: string;
  category: AchievementCategory;
  icon: AchievementIconName;
  tint: string;
};

export const ACHIEVEMENT_CATALOG: Record<string, AchievementCatalogEntry> = {
  ads_first_ad_logged_ever: {
    label: "First ad logged",
    category: "ads",
    icon: "Zap",
    tint: "text-emerald-700 dark:text-emerald-300",
  },
  ads_first_goal_met_today: {
    label: "Daily goal met",
    category: "ads",
    icon: "Star",
    tint: "text-amber-700 dark:text-amber-300",
  },
  ads_above_and_beyond_today: {
    label: "Above & beyond",
    category: "ads",
    icon: "Trophy",
    tint: "text-yellow-700 dark:text-yellow-200",
  },
  ads_logging_streak_milestone: {
    label: "Streak milestone",
    category: "ads",
    icon: "Flame",
    tint: "text-orange-700 dark:text-orange-300",
  },
  ads_first_sale_claim_ever: {
    label: "First sale logged",
    category: "sales",
    icon: "DollarSign",
    tint: "text-emerald-700 dark:text-emerald-300",
  },
  ads_sale_claim_logged_today: {
    label: "Sale logged today",
    category: "sales",
    icon: "DollarSign",
    tint: "text-teal-700 dark:text-teal-300",
  },
  kb_first_gap_resolved: {
    label: "First KB gap resolved",
    category: "kb",
    icon: "BookOpen",
    tint: "text-indigo-700 dark:text-indigo-300",
  },
  ai_prompt_suggestion_first: {
    label: "First prompt suggestion",
    category: "prompting",
    icon: "Sparkles",
    tint: "text-violet-700 dark:text-violet-300",
  },
  ai_prompt_suggestion_accepted_first: {
    label: "First suggestion accepted",
    category: "prompting",
    icon: "Trophy",
    tint: "text-emerald-700 dark:text-emerald-300",
  },
  training_roleplay_session_logged: {
    label: "Roleplay session logged",
    category: "training",
    icon: "GraduationCap",
    tint: "text-violet-700 dark:text-violet-300",
  },
  training_roleplay_reviewed_pass: {
    label: "Roleplay passed review",
    category: "training",
    icon: "GraduationCap",
    tint: "text-violet-700 dark:text-violet-200",
  },

  // Phase 7 — onboarding + cross-domain catalog growth.
  user_first_login_ever: {
    label: "First login",
    category: "user",
    icon: "Sparkles",
    tint: "text-fuchsia-700 dark:text-fuchsia-300",
  },
  user_login_streak_milestone: {
    label: "Login streak",
    category: "user",
    icon: "Calendar",
    tint: "text-sky-700 dark:text-sky-300",
  },
  ads_first_template_use: {
    label: "First template use",
    category: "ads",
    icon: "LayoutTemplate",
    tint: "text-emerald-700 dark:text-emerald-300",
  },
  ads_first_media_attached: {
    label: "First media attached",
    category: "ads",
    icon: "Image",
    tint: "text-cyan-700 dark:text-cyan-300",
  },
  ads_weekly_goal_hit: {
    label: "Weekly goal hit",
    category: "ads",
    icon: "Target",
    tint: "text-amber-700 dark:text-amber-300",
  },
  training_first_kb_question_asked: {
    label: "First question to KB",
    category: "training",
    icon: "MessageCircleQuestion",
    tint: "text-indigo-700 dark:text-indigo-300",
  },
  training_first_roleplay_pass_ever: {
    label: "First roleplay pass",
    category: "training",
    icon: "Trophy",
    tint: "text-violet-700 dark:text-violet-300",
  },
  kb_gap_resolved_milestone: {
    label: "KB gap milestone",
    category: "kb",
    icon: "BookOpen",
    tint: "text-indigo-700 dark:text-indigo-200",
  },
  lead_first_logged_ever: {
    label: "First lead logged",
    category: "lead",
    icon: "UserPlus",
    tint: "text-rose-700 dark:text-rose-300",
  },
  lead_first_sent_to_ghl: {
    label: "First lead synced",
    category: "lead",
    icon: "Send",
    tint: "text-pink-700 dark:text-pink-300",
  },
  lead_logged_count_milestone: {
    label: "Leads milestone",
    category: "lead",
    icon: "UserPlus",
    tint: "text-rose-700 dark:text-rose-200",
  },
};

export function resolveAchievementLabel(
  achievementKey: string,
  metadata?: Record<string, unknown>,
): string {
  if (achievementKey === "ads_logging_streak_milestone") {
    const days = metadata?.days;
    if (typeof days === "number" && Number.isFinite(days)) return `${days}-day streak`;
    return "Streak milestone";
  }
  if (achievementKey === "user_login_streak_milestone") {
    const days = metadata?.days;
    if (typeof days === "number" && Number.isFinite(days)) return `${days}-day login streak`;
    return "Login streak";
  }
  if (achievementKey === "kb_gap_resolved_milestone") {
    const count = metadata?.count;
    if (typeof count === "number" && Number.isFinite(count)) return `${count} KB gaps resolved`;
    return "KB gap milestone";
  }
  if (achievementKey === "lead_logged_count_milestone") {
    const count = metadata?.count;
    if (typeof count === "number" && Number.isFinite(count)) return `${count} leads logged`;
    return "Leads milestone";
  }
  if (achievementKey === "ads_weekly_goal_hit") {
    const week = metadata?.iso_week;
    if (typeof week === "string" && week) return `Weekly goal hit (${week})`;
    return "Weekly goal hit";
  }
  return ACHIEVEMENT_CATALOG[achievementKey]?.label ?? achievementKey.replaceAll("_", " ");
}

export function getAchievementCatalogEntry(key: string): AchievementCatalogEntry {
  return (
    ACHIEVEMENT_CATALOG[key] ?? {
      label: key.replaceAll("_", " "),
      category: "ads",
      icon: "Award",
      tint: "text-blue-700 dark:text-blue-300",
    }
  );
}
